/**
 * useChatSound — global preference + tiny Web-Audio "ding" for
 * incoming chat messages. Uses localStorage so the choice persists
 * per-device. Uses a synthesized two-tone chime (no asset needed).
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "DOSERNE_CHAT_SOUND_ON";

let sharedCtx = null;
function getCtx() {
  try {
    const Klass = window.AudioContext || window.webkitAudioContext;
    if (!Klass) return null;
    if (!sharedCtx) sharedCtx = new Klass();
    // Some browsers auto-suspend; resume opportunistically.
    if (sharedCtx.state === "suspended") sharedCtx.resume().catch(() => {});
    return sharedCtx;
  } catch { return null; }
}

/** Play a soft Messenger-style two-tone "ding". */
export function playDing() {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime;
  const notes = [
    { freq: 880, start: 0.0, dur: 0.14 },
    { freq: 1174, start: 0.09, dur: 0.18 },
  ];
  const master = ctx.createGain();
  master.gain.value = 0.18;
  master.connect(ctx.destination);
  for (const n of notes) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = n.freq;
    g.gain.setValueAtTime(0.0001, t0 + n.start);
    g.gain.exponentialRampToValueAtTime(1.0, t0 + n.start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.dur);
    osc.connect(g).connect(master);
    osc.start(t0 + n.start);
    osc.stop(t0 + n.start + n.dur + 0.02);
  }
}

export function useChatSound() {
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const v = localStorage.getItem(KEY);
      return v === null ? true : v === "1";
    } catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, soundOn ? "1" : "0"); } catch { /* ignore */ }
  }, [soundOn]);

  const toggleSound = useCallback(() => setSoundOn((v) => !v), []);
  const play = useCallback(() => { if (soundOn) playDing(); }, [soundOn]);

  return { soundOn, toggleSound, play };
}
